package com.example.hukakmp.data

import com.example.hukakmp.model.PartePesca
import com.example.hukakmp.model.Tournament
import com.example.hukakmp.model.TournamentParticipant
import com.example.hukakmp.model.TournamentParticipantStatus
import com.example.hukakmp.model.TournamentReport
import com.example.hukakmp.model.TournamentReportStatus
import com.example.hukakmp.model.TournamentSpecies
import com.example.hukakmp.model.TournamentScoreType
import com.example.hukakmp.model.TournamentSummary
import com.example.hukakmp.model.scoreTypeEnum
import com.example.hukakmp.model.status
import com.example.hukakmp.model.statusEnum
import com.example.hukakmp.util.parsearFecha
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.serialization.Serializable

private const val TOURNAMENTS_COLLECTION = "torneos"
private const val TOURNAMENT_CODES_COLLECTION = "torneo_codigos"
private const val PARTICIPANTS_COLLECTION = "participantes"
private const val REPORTS_COLLECTION = "partes"

@Serializable
private data class TournamentCodeLookup(
    val tournamentId: String = "",
    val creatorId: String = "",
)

class TournamentRepository {
    private val firestore = Firebase.firestore

    suspend fun getMyTournaments(): Result<List<TournamentSummary>> = runCatching {
        val user = Firebase.auth.currentUser ?: error("Usuario no autenticado")
        val result = mutableListOf<TournamentSummary>()

        val asCreator = firestore.collection(TOURNAMENTS_COLLECTION)
            .where { "creatorId" equalTo user.uid }
            .get()

        for (document in asCreator.documents) {
            val tournament = document.data<Tournament>().copy(id = document.id)
            val participants = getParticipants(tournament.id, acceptedOnly = false)
            val reports = getTournamentReports(tournament.id)
            result += TournamentSummary(
                tournament = tournament,
                participants = participants,
                reports = reports,
                isCreator = true,
            )
        }

        val asParticipant = firestore.collectionGroup(PARTICIPANTS_COLLECTION)
            .where { "userId" equalTo user.uid }
            .get()

        for (document in asParticipant.documents) {
            val tournamentId = document.reference.parent.parent?.id ?: continue
            if (result.any { it.tournament.id == tournamentId }) continue

            val participant = document.data<TournamentParticipant>()
            val tournamentDoc = firestore.document("${TOURNAMENTS_COLLECTION}/${tournamentId}").get()
            if (!tournamentDoc.exists) continue

            val tournament = tournamentDoc.data<Tournament>().copy(id = tournamentId)
            val participants = getParticipants(tournamentId, acceptedOnly = true)
            val reports = getUserTournamentReports(tournamentId, user.uid)
            val accepted = participants
                .filter { it.statusEnum() == TournamentParticipantStatus.ACEPTADO }
                .sortedByDescending { it.puntaje }
            val position = accepted.indexOfFirst { it.userId == user.uid }
                .let { if (it >= 0) it + 1 else 0 }

            result += TournamentSummary(
                tournament = tournament,
                participants = participants,
                reports = reports,
                isCreator = false,
                myStatus = participant.statusEnum(),
                myScore = participant.puntaje,
                myPosition = position,
            )
        }

        result.sortedByDescending { it.tournament.creadoEn.seconds }
    }

    suspend fun createTournament(
        name: String,
        description: String,
        startDate: String,
        endDate: String,
        scoreType: TournamentScoreType,
    ): Result<Tournament> = runCatching {
        val user = Firebase.auth.currentUser ?: error("Usuario no autenticado")
        val cleanName = name.trim()
        require(cleanName.length >= 3) { "Ingresá un nombre para el torneo." }

        val start = parsearFecha(startDate) ?: error("La fecha de inicio no es válida.")
        val end = parsearFecha(endDate) ?: error("La fecha de fin no es válida.")
        require(end >= start) { "La fecha de fin no puede ser anterior al inicio." }

        val zone = TimeZone.currentSystemDefault()
        val startInstant = start.atStartOfDayIn(zone)
        val endInstant = end.atStartOfDayIn(zone)
        val ref = firestore.collection(TOURNAMENTS_COLLECTION).document
        val code = generateInvitationCode()

        val tournament = Tournament(
            id = ref.id,
            nombre = cleanName,
            descripcion = description.trim(),
            creatorId = user.uid,
            creatorName = user.displayName?.takeIf { it.isNotBlank() } ?: "Organizador",
            fechaInicio = Timestamp(startInstant.epochSeconds, 0),
            fechaFin = Timestamp(endInstant.epochSeconds + 86_399L, 999_000_000),
            tipoPuntaje = scoreType.name,
            codigoInvitacion = code,
            creadoEn = Timestamp.now(),
        )

        val codeRef = firestore.document("$TOURNAMENT_CODES_COLLECTION/$code")
        val creatorParticipantRef = firestore.document(
            "$TOURNAMENTS_COLLECTION/${ref.id}/$PARTICIPANTS_COLLECTION/${user.uid}"
        )

        firestore.runTransaction {
            val existingCode = get(codeRef)
            require(!existingCode.exists) {
                "No se pudo generar un código único. Intentá crear el torneo nuevamente."
            }

            set(
                documentRef = ref,
                strategy = Tournament.serializer(),
                data = tournament,
            )
            set(
                documentRef = codeRef,
                strategy = TournamentCodeLookup.serializer(),
                data = TournamentCodeLookup(
                    tournamentId = ref.id,
                    creatorId = user.uid,
                ),
            )
            set(
                documentRef = creatorParticipantRef,
                strategy = TournamentParticipant.serializer(),
                data = TournamentParticipant(
                    userId = user.uid,
                    userName = user.displayName?.takeIf { it.isNotBlank() } ?: "Organizador",
                    userPhoto = user.photoURL ?: "",
                    estado = TournamentParticipantStatus.ACEPTADO.name,
                    solicitadoEn = Timestamp.now(),
                    respondidoEn = Timestamp.now(),
                ),
            )
        }

        tournament
    }

    suspend fun searchByCode(code: String): Result<Tournament?> = runCatching {
        val normalized = code.trim().uppercase()
        if (normalized.isBlank()) return@runCatching null

        val lookupSnapshot = firestore
            .document("$TOURNAMENT_CODES_COLLECTION/$normalized")
            .get()

        if (!lookupSnapshot.exists) {
            return@runCatching null
        }

        val lookup = lookupSnapshot.data<TournamentCodeLookup>()
        if (lookup.tournamentId.isBlank()) {
            return@runCatching null
        }

        val tournamentSnapshot = firestore
            .document("$TOURNAMENTS_COLLECTION/${lookup.tournamentId}")
            .get()

        if (!tournamentSnapshot.exists) {
            null
        } else {
            tournamentSnapshot.data<Tournament>().copy(id = tournamentSnapshot.id)
        }
    }

    suspend fun requestJoin(tournamentId: String): Result<Unit> = runCatching {
        val user = Firebase.auth.currentUser ?: error("Usuario no autenticado")
        val tournament = firestore.document("${TOURNAMENTS_COLLECTION}/${tournamentId}").get()
        require(tournament.exists) { "El torneo ya no está disponible." }

        val ref = firestore.document(
            "${TOURNAMENTS_COLLECTION}/${tournamentId}/${PARTICIPANTS_COLLECTION}/${user.uid}"
        )
        if (ref.get().exists) error("Ya tenés una solicitud para este torneo.")

        ref.set(
            TournamentParticipant(
                userId = user.uid,
                userName = user.displayName?.takeIf { it.isNotBlank() } ?: "Pescador",
                userPhoto = user.photoURL ?: "",
                estado = TournamentParticipantStatus.PENDIENTE.name,
                solicitadoEn = Timestamp.now(),
            )
        )
    }

    suspend fun respondRequest(
        tournamentId: String,
        participantId: String,
        accept: Boolean,
    ): Result<Unit> = runCatching {
        val currentUid = Firebase.auth.currentUser?.uid ?: error("Usuario no autenticado")
        val tournamentSnapshot = firestore.document("${TOURNAMENTS_COLLECTION}/${tournamentId}").get()
        require(tournamentSnapshot.exists) { "El torneo ya no está disponible." }
        val tournament = tournamentSnapshot.data<Tournament>()
        require(tournament.creatorId == currentUid) { "Solo el organizador puede responder solicitudes." }

        val ref = firestore.document(
            "${TOURNAMENTS_COLLECTION}/${tournamentId}/${PARTICIPANTS_COLLECTION}/${participantId}"
        )
        val snapshot = ref.get()
        require(snapshot.exists) { "La solicitud ya no está disponible." }
        val participant = snapshot.data<TournamentParticipant>()

        ref.set(
            participant.copy(
                estado = if (accept) {
                    TournamentParticipantStatus.ACEPTADO.name
                } else {
                    TournamentParticipantStatus.RECHAZADO.name
                },
                respondidoEn = Timestamp.now(),
            ),
            merge = true,
        )
    }


    /**
     * Replica el comportamiento de Huka Android: un parte guardado suma
     * automáticamente en todos los torneos activos donde el usuario puede participar.
     *
     * El alta del parte del torneo y el incremento del participante se realizan en
     * una misma transacción. Un fallo aquí no debe invalidar el parte principal.
     */
    suspend fun onReportSaved(report: PartePesca): Result<Int> = runCatching {
        val user = Firebase.auth.currentUser ?: error("Usuario no autenticado")
        val reportId = report.id?.takeIf { it.isNotBlank() }
            ?: error("El parte no tiene un identificador válido.")

        val activeTournaments = getMyTournaments().getOrThrow().filter { summary ->
            summary.tournament.status() == com.example.hukakmp.model.TournamentStatus.ACTIVO &&
                (
                    summary.isCreator ||
                        summary.myStatus == TournamentParticipantStatus.ACEPTADO
                    )
        }

        var registered = 0
        for (summary in activeTournaments) {
            val saved = runCatching {
                saveReportInTournament(
                    summary = summary,
                    report = report,
                    reportId = reportId,
                    userId = user.uid,
                    userName = user.displayName?.takeIf { it.isNotBlank() } ?: "Pescador",
                    userPhoto = user.photoURL ?: "",
                )
            }.getOrDefault(false)

            if (saved) registered += 1
        }

        registered
    }

    private suspend fun saveReportInTournament(
        summary: TournamentSummary,
        report: PartePesca,
        reportId: String,
        userId: String,
        userName: String,
        userPhoto: String,
    ): Boolean {
        val tournament = summary.tournament
        val isFirstReport = tournament.reglas?.bonusPrimerParte?.let {
            isFirstTournamentReport(tournament.id)
        } ?: false

        val score = calculateScore(
            tournament = tournament,
            report = report,
            isFirstReport = isFirstReport,
        )

        val tournamentReport = TournamentReport(
            parteId = reportId,
            userId = userId,
            userName = userName,
            fecha = report.fecha.orEmpty(),
            especies = report.peces
                .filter { it.especie.isNotBlank() && it.cantidad > 0 }
                .map {
                    TournamentSpecies(
                        nombre = it.especie,
                        cantidad = it.cantidad,
                    )
                },
            // Las fotos permanecen en el parte privado del pescador.
            // No copiamos download URLs con token a documentos compartidos del torneo.
            fotos = emptyList(),
            puntaje = score,
            estado = TournamentReportStatus.ACTIVO.name,
            creadoEn = Timestamp.now(),
        )

        val reportRef = firestore.document(
            TOURNAMENTS_COLLECTION + "/" + tournament.id + "/" +
                REPORTS_COLLECTION + "/" + reportId
        )
        val participantRef = firestore.document(
            TOURNAMENTS_COLLECTION + "/" + tournament.id + "/" +
                PARTICIPANTS_COLLECTION + "/" + userId
        )

        return firestore.runTransaction {
            val existingReport = get(reportRef)
            if (existingReport.exists) {
                return@runTransaction false
            }

            val participantSnapshot = get(participantRef)
            val now = Timestamp.now()

            val participant = if (participantSnapshot.exists) {
                participantSnapshot.data<TournamentParticipant>()
            } else {
                TournamentParticipant(
                    userId = userId,
                    userName = userName,
                    userPhoto = userPhoto,
                    estado = TournamentParticipantStatus.ACEPTADO.name,
                    solicitadoEn = now,
                    respondidoEn = now,
                )
            }

            val alreadyCounted = reportId in participant.parteIds
            val updatedParticipant = participant.copy(
                userName = participant.userName.ifBlank { userName },
                userPhoto = participant.userPhoto.ifBlank { userPhoto },
                estado = if (summary.isCreator) {
                    TournamentParticipantStatus.ACEPTADO.name
                } else {
                    participant.estado
                },
                puntaje = participant.puntaje + if (alreadyCounted) 0 else score,
                parteIds = if (alreadyCounted) {
                    participant.parteIds
                } else {
                    participant.parteIds + reportId
                },
            )

            set(
                documentRef = reportRef,
                strategy = TournamentReport.serializer(),
                data = tournamentReport,
            )
            set(
                documentRef = participantRef,
                strategy = TournamentParticipant.serializer(),
                data = updatedParticipant,
                merge = true,
            )
            true
        }
    }

    private suspend fun isFirstTournamentReport(tournamentId: String): Boolean =
        firestore.collection(
            TOURNAMENTS_COLLECTION + "/" + tournamentId + "/" + REPORTS_COLLECTION
        )
            .limit(1)
            .get()
            .documents
            .isEmpty()

    private fun calculateScore(
        tournament: Tournament,
        report: PartePesca,
        isFirstReport: Boolean,
    ): Int = when (tournament.scoreTypeEnum()) {
        TournamentScoreType.CANTIDAD_PECES ->
            report.peces.sumOf { it.cantidad.coerceAtLeast(0) }

        TournamentScoreType.ESPECIES_DISTINTAS ->
            report.peces
                .filter { it.cantidad > 0 && it.especie.isNotBlank() }
                .map { normalizeSpeciesKey(it.especie) }
                .distinct()
                .size

        TournamentScoreType.PERSONALIZADO -> {
            val rules = tournament.reglas
            if (rules == null || !rules.hasAnyRule) {
                0
            } else {
                var total = 0

                if (isFirstReport) {
                    total += rules.bonusPrimerParte ?: 0
                }

                rules.puntosPorPez?.let { pointsPerFish ->
                    total += report.peces.sumOf {
                        it.cantidad.coerceAtLeast(0)
                    } * pointsPerFish
                }

                rules.puntosPorEspecie
                    ?.takeIf { it.isNotEmpty() }
                    ?.let { table ->
                        report.peces.forEach { capture ->
                            val key = normalizeSpeciesKey(capture.especie)
                            val pointsPerFish =
                                table[key] ?: rules.puntosOtrosPeces
                            total += pointsPerFish * capture.cantidad.coerceAtLeast(0)
                        }
                    }

                total
            }
        }
    }

    private fun normalizeSpeciesKey(name: String): String =
        name.lowercase()
            .replace("á", "a")
            .replace("é", "e")
            .replace("í", "i")
            .replace("ó", "o")
            .replace("ú", "u")
            .replace("ñ", "n")
            .replace(Regex("[^a-z0-9]+"), "_")
            .trim('_')


    suspend fun rejectReport(
        tournamentId: String,
        reportId: String,
        reason: String = "",
    ): Result<Unit> = runCatching {
        val currentUid = Firebase.auth.currentUser?.uid ?: error("Usuario no autenticado")

        val tournamentRef = firestore.document(
            TOURNAMENTS_COLLECTION + "/" + tournamentId
        )
        val tournamentSnapshot = tournamentRef.get()
        require(tournamentSnapshot.exists) { "El torneo ya no está disponible." }

        val tournament = tournamentSnapshot.data<Tournament>()
        require(tournament.creatorId == currentUid) {
            "Solo el organizador puede rechazar partes."
        }

        val reportRef = firestore.document(
            TOURNAMENTS_COLLECTION + "/" + tournamentId + "/" +
                REPORTS_COLLECTION + "/" + reportId
        )
        val reportSnapshot = reportRef.get()
        require(reportSnapshot.exists) { "El parte ya no está disponible." }

        val report = reportSnapshot.data<TournamentReport>()
        if (report.statusEnum() == TournamentReportStatus.RECHAZADO) {
            return@runCatching Unit
        }

        // El cliente solo cambia el estado del parte. El ajuste de puntaje y
        // parteIds lo realiza una Cloud Function con credenciales de confianza.
        reportRef.set(
            report.copy(
                estado = TournamentReportStatus.RECHAZADO.name,
                motivoRechazo = reason.trim(),
            ),
            merge = true,
        )
    }


    private suspend fun getTournamentReports(tournamentId: String): List<TournamentReport> =
        runCatching {
            firestore.collection(
                TOURNAMENTS_COLLECTION + "/" + tournamentId + "/" + REPORTS_COLLECTION
            ).get().documents.mapNotNull { document ->
                runCatching {
                    document.data<TournamentReport>().copy(
                        parteId = document.data<TournamentReport>().parteId.ifBlank { document.id }
                    )
                }.getOrNull()
            }
        }.getOrDefault(emptyList())

    private suspend fun getUserTournamentReports(
        tournamentId: String,
        userId: String,
    ): List<TournamentReport> =
        runCatching {
            firestore.collection(
                TOURNAMENTS_COLLECTION + "/" + tournamentId + "/" + REPORTS_COLLECTION
            )
                .where { "userId" equalTo userId }
                .get()
                .documents
                .mapNotNull { document ->
                    runCatching {
                        document.data<TournamentReport>().copy(
                            parteId = document.data<TournamentReport>().parteId.ifBlank { document.id }
                        )
                    }.getOrNull()
                }
        }.getOrDefault(emptyList())

    private suspend fun getParticipants(
        tournamentId: String,
        acceptedOnly: Boolean,
    ): List<TournamentParticipant> = runCatching {
        val collection = firestore.collection(
            "$TOURNAMENTS_COLLECTION/$tournamentId/$PARTICIPANTS_COLLECTION"
        )

        val snapshot = if (acceptedOnly) {
            collection
                .where { "estado" equalTo TournamentParticipantStatus.ACEPTADO.name }
                .get()
        } else {
            collection.get()
        }

        snapshot.documents.mapNotNull { document ->
            runCatching { document.data<TournamentParticipant>() }.getOrNull()
        }
    }.getOrDefault(emptyList())

    private fun generateInvitationCode(): String {
        val chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"
        return "HUKA-" + (1..6).joinToString("") { chars.random().toString() }
    }
}
